import type { ReactNode } from "react";
import { Box, Flex, Paper, Stack, Text } from "@mantine/core";
import { IconShieldCheck } from "@tabler/icons-react";

export type CompanyAuthorityAcceptanceRow = {
  id: string;
  companyId: string;
  userId: string;
  companyName: string;
  userName: string;
  email: string;
  message: string;
  acceptedAt: Date | string;
};

function formatAcceptedAt(value: Date | string): string {
  const d = typeof value === "string" ? new Date(value) : value;
  return d.toLocaleString("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function Field({ label, value }: { label: string; value: ReactNode }) {
  return (
    <Flex direction="column" gap={4}>
      <Text size="xs" c="slate.6" tt="uppercase" fw={600}>
        {label}
      </Text>
      <Box>{value}</Box>
    </Flex>
  );
}

interface AdminCompanyAuthorityAcceptanceTabProps {
  records: CompanyAuthorityAcceptanceRow[];
}

export default function AdminCompanyAuthorityAcceptanceTab({ records }: AdminCompanyAuthorityAcceptanceTabProps) {
  if (!records.length) {
    return (
      <Text c="dimmed" size="sm">
        No company authority acceptance records.
      </Text>
    );
  }

  return (
    <Stack gap="xl">
      {records.map((r) => (
        <Paper
          key={r.id}
          p={0}
          radius="lg"
          style={{
            overflow: "hidden",
            border: "1px solid #E5E7EB",
            boxShadow: "0 1px 3px rgba(15, 23, 42, 0.08)",
          }}
        >
          <Flex
            align="center"
            gap="sm"
            px={{ base: "md", sm: "xl" }}
            py={{ base: "md", sm: "lg" }}
            style={{ backgroundColor: "#04305c" }}
          >
            <IconShieldCheck size={22} color="#ffc300" stroke={1.5} />
            <Text fw={700} fz="md" c="#ffc300" style={{ letterSpacing: 0.2 }}>
              Record · {formatAcceptedAt(r.acceptedAt)}
            </Text>
          </Flex>
          <Stack gap="md" p={{ base: "lg", sm: "xl" }}>
            <Field label="ID" value={<Text size="sm">{r.id}</Text>} />
            <Field label="Company ID" value={<Text size="sm">{r.companyId}</Text>} />
            <Field label="User ID" value={<Text size="sm">{r.userId}</Text>} />
            <Field label="Company name" value={<Text size="sm">{r.companyName}</Text>} />
            <Field label="User name" value={<Text size="sm">{r.userName}</Text>} />
            <Field label="Email" value={<Text size="sm">{r.email}</Text>} />
            <Field
              label="Accepted at"
              value={<Text size="sm">{formatAcceptedAt(r.acceptedAt)}</Text>}
            />
            <Field
              label="Message"
              value={
                <Text size="sm" style={{ lineHeight: 1.6, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                  {r.message || "N/A"}
                </Text>
              }
            />
          </Stack>
        </Paper>
      ))}
    </Stack>
  );
}
